import React from 'react';
import ReportTable, {
  ColumnWidth,
  SortingParams,
  useSorting,
} from '../MalpracticeReports/components/ReportTable/ReportTable';
import { StyledInputContainer } from '../WorkDownloading/components/WorkDownloading.styles';
import { LineClamp, StyledParagraph } from './SubmissionFailures.styles';
import { AssessmentState, BusinessUnit, DATE_FORMAT, SortOrder } from '@cambridgeassessment/lib-ee-commons';
import { useDispatch } from 'react-redux';
import * as actions from '../../../store/rmaSubmissionFailures/actions';
import { RmaErrors, SearchValues } from '../../../types/RmaErrors';
import * as thunks from '../../../store/rmaSubmissionFailures/thunks';
import Loader from '../../../components/Loader/Loader';
import { Tooltip } from '@cambridgeassessment/cambridge-ui';
import moment from 'moment';
import { isEmpty } from 'lodash';

interface Props {
  rmaErrors: RmaErrors[] | null;
  businessUnitId: BusinessUnit['id'];
  inputValues: SearchValues;
  isRmaLoading: boolean;
  hasNextUrl: boolean;
}

const FailedSubmissionTable: React.FC<Props> = ({
  rmaErrors,
  businessUnitId,
  inputValues,
  isRmaLoading,
  hasNextUrl,
}) => {
  const dispatch = useDispatch();

  const { sort: onSort, reportSummarySortingParams } = useSorting<RmaErrors>(
    (sortingParams) => {
      dispatch(actions.resetState());
      loadMoreItems(sortingParams);
    },
    {
      fieldName: 'assessmentComponent',
      order: SortOrder.ASC,
    },
  );

  const loadMoreItems = (sortingParams?: SortingParams<RmaErrors>): void => {
    dispatch(
      thunks.getSectionsWithRmaErrors(
        businessUnitId,
        inputValues,
        [AssessmentState.FAILED_TO_SEND_FOR_ASSESSMENT, AssessmentState.READY_FOR_RM_RERUN],
        sortingParams || reportSummarySortingParams,
      ),
    );
  };

  if (isRmaLoading) {
    return <Loader />;
  }

  if (rmaErrors == null || rmaErrors.length === 0) {
    return (
      <StyledInputContainer>
        <StyledParagraph data-qa-id='no-results-title'>No results found.</StyledParagraph>
      </StyledInputContainer>
    );
  }

  return (
    <ReportTable
      data={rmaErrors}
      columns={[
        {
          label: 'Syllabus, Component',
          name: 'assessmentComponent',
          centre: false,
          sortable: true,
        },
        {
          label: 'Centre Number',
          name: 'centreId',
          centre: false,
          sortable: true,
        },
        {
          label: 'Candidate Number',
          name: 'learnerNumber',
          centre: false,
          sortable: true,
        },
        {
          label: 'Section name',
          name: 'sectionName',
          centre: false,
          sortable: true,
        },
        {
          label: 'Filename',
          name: 'filename',
          centre: false,
          sortable: false,
        },
        {
          label: 'Error',
          name: 'rmaError',
          centre: false,
          sortable: true,
          renderItem: (error) => {
            return (
              <Tooltip title={error.rmaError.toString()} arrow={true} placement='top' dataQaId='tooltip' interactive>
                <LineClamp linesCount={3}>{error.rmaError}</LineClamp>
              </Tooltip>
            );
          },
        },
        {
          label: 'Last date submitted to RM ',
          name: 'submissionDate',
          centre: false,
          sortable: false,
          width: ColumnWidth.Wide,
          renderItem: (error) => {
            if (error.state === AssessmentState.READY_FOR_RM_RERUN) {
              return 'In Progress';
            }

            if (!isEmpty(error.submissionDate)) {
              return moment(error.submissionDate).format(DATE_FORMAT);
            }
          },
        },
        {
          label: 'Last date submitted by',
          name: 'submittedBy',
          centre: false,
          sortable: false,
          width: ColumnWidth.Wide,
        },
      ]}
      loadMoreItems={loadMoreItems}
      onSort={onSort}
      hasMoreItems={hasNextUrl}
    />
  );
};

export default FailedSubmissionTable;
